import React, { createContext, useContext, useState, useEffect } from "react";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState("fr");

  useEffect(() => {
    // Auto-detect language based on browser locale, default to French for Canada
    const browserLang = navigator.language || navigator.userLanguage;
    const detectedLang = browserLang.startsWith("en") && !browserLang.includes("CA") ? "en" : "fr";
    setLanguage(detectedLang);
  }, []);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "fr" ? "en" : "fr"));
  };

  const t = (key) => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  fr: {
    // Navigation
    home: "Accueil",
    products: "Produits",
    shop: "Boutique",
    sectors: "Secteurs",
    caseStudies: "Réalisations",
    about: "À propos",
    resources: "Ressources",
    contact: "Contact",
    
    // Hero Section
    heroTitle: "Solutions de levage industriel sur mesure",
    heroSubtitle: "Expertise, fiabilité et sécurité pour tous vos besoins de manutention.",
    heroSlogan: "La force maîtrisée, la sécurité garantie",
    getQuote: "Demander un devis",
    discoverSolutions: "Découvrir nos solutions",
    
    // Products
    overheadCranes: "Ponts roulants",
    overheadCranesDesc: "Ponts roulants robustes et personnalisés de 3t à 40t et sur mesure",
    palansTreeuils: "Palans et treuils électriques", 
    palansTreeuilsDesc: "Palans et treuils électriques pour une manipulation précise et sécuritaire",
    systemesManutention: "Systèmes de manutention lourde",
    systemesManutentionDesc: "Rails, chariots et convoyeurs de levage pour manutention industrielle",
    paniersSupports: "Paniers et supports de levage spécialisés",
    paniersSupportsDesc: "Solutions spécialisées pour gaz, pièces lourdes et applications techniques",
    maintenance: "Maintenance, inspection et certification",
    maintenanceDesc: "Services complets de maintenance, inspection et certification CSA/ASME",
    
    // Stats
    yearsExperience: "Années d'expérience",
    projectsDelivered: "Projets livrés",
    complianceRate: "Taux de conformité",
    
    // Contact
    phone: "(514) 899-5058",
    email: "info@excel-levage.com",
    address: "9320 St Laurent Blvd, Suite 200, Montréal, Québec H2N 1N7",
    
    // Footer
    followUs: "Suivez-nous",
    allRightsReserved: "Tous droits réservés",
    
    // About
    aboutTitle: "Excellence en levage industriel",
    aboutDescription: "Excel Levage, spécialiste québécois des équipements de levage, accompagne ses clients avec des solutions robustes et sécuritaires.",
    ourValues: "Nos valeurs",
    safety: "Sécurité",
    reliability: "Fiabilité",
    performance: "Performance",
    customerService: "Service client",
    
    // Forms
    name: "Nom",
    emailLabel: "Email",
    phoneLabel: "Téléphone",
    subject: "Sujet",
    message: "Message",
    sendRequest: "Envoyer ma demande",
    equipmentType: "Type d'équipement",
    capacity: "Capacité",
    sector: "Secteur",
    deadline: "Échéance",
    
    // Case studies
    caseStudy1Title: "Pont roulant 10t — secteur manufacturier",
    caseStudy1Desc: "Installation d'un pont roulant pour optimiser la productivité manufacturière",
    caseStudy2Title: "Paniers de levage gaz SF6 — secteur énergie",
    caseStudy2Desc: "Solutions spécialisées pour manipulation sécuritaire du gaz SF6",
    caseStudy3Title: "Palans motorisés — atelier mécanique",
    caseStudy3Desc: "Amélioration de l'ergonomie et réduction des risques en atelier",
    
    // Resources
    resourcesTitle: "Guides et ressources",
    guide1: "Choisir le bon pont roulant",
    guide2: "Normes et conformité (CSA / ASME)",
    guide3: "Checklist maintenance préventive",
    downloadGuides: "Télécharger nos guides",
    
    // FAQ
    faq1: "Faites-vous des projets sur mesure ?",
    faq2: "Offrez-vous inspection & certification ?",
    faq3: "Quelles capacités maximales installez-vous ?",
    
    // Sectors
    manufacturing: "Manufacturier",
    manufacturingDesc: "Acier, métal, transformation",
    agrifood: "Agroalimentaire",
    agrifoodDesc: "Solutions adaptées à l'industrie alimentaire",
    energy: "Énergie",
    energyDesc: "Hydro, gaz, électricité",
    transport: "Transport & logistique",
    transportDesc: "Solutions pour la logistique",
    construction: "Bâtiment et construction",
    constructionDesc: "Équipements pour le secteur du bâtiment",
    
    // Error messages
    error: "Une erreur est survenue"
  },
  en: {
    // Navigation
    home: "Home",
    products: "Products",
    shop: "Shop",
    sectors: "Sectors",
    caseStudies: "Case Studies",
    about: "About",
    resources: "Resources",
    contact: "Contact",
    
    // Hero Section
    heroTitle: "Custom industrial lifting solutions",
    heroSubtitle: "Expertise, reliability and safety for all your handling needs.",
    heroSlogan: "Controlled strength, guaranteed safety",
    getQuote: "Get a Quote",
    discoverSolutions: "Discover our solutions",
    
    // Products
    overheadCranes: "Overhead Cranes",
    overheadCranesDesc: "Robust and customized overhead cranes from 3t to 40t and custom solutions",
    palansTreeuils: "Electric hoists and winches",
    palansTreeuilsDesc: "Electric hoists and winches for precise and safe material handling",
    systemesManutention: "Heavy handling systems", 
    systemesManutentionDesc: "Rails, trolleys and lifting conveyors for industrial material handling",
    paniersSupports: "Specialized lifting baskets and supports",
    paniersSupportsDesc: "Specialized solutions for gas, heavy parts and technical applications",
    maintenance: "Maintenance, inspection and certification",
    maintenanceDesc: "Complete maintenance, inspection and CSA/ASME certification services",
    
    // Stats
    yearsExperience: "Years of experience",
    projectsDelivered: "Projects delivered",
    complianceRate: "Compliance rate",
    
    // Contact
    phone: "(514) 899-5058",
    email: "info@excel-levage.com",
    address: "9320 St Laurent Blvd, Suite 200, Montreal, Quebec H2N 1N7",
    
    // Footer
    followUs: "Follow us",
    allRightsReserved: "All rights reserved",
    
    // About
    aboutTitle: "Excellence in industrial lifting",
    aboutDescription: "Excel Levage, a Quebec-based lifting equipment specialist, delivers robust and safe solutions for demanding industries.",
    ourValues: "Our values",
    safety: "Safety",
    reliability: "Reliability",
    performance: "Performance",
    customerService: "Customer service",
    
    // Forms
    name: "Name",
    emailLabel: "Email",
    phoneLabel: "Phone",
    subject: "Subject",
    message: "Message",
    sendRequest: "Send my request",
    equipmentType: "Equipment type",
    capacity: "Capacity",
    sector: "Sector",
    deadline: "Deadline",
    
    // Case studies
    caseStudy1Title: "10t Overhead Crane — Manufacturing Sector",
    caseStudy1Desc: "Overhead crane installation to optimize manufacturing productivity",
    caseStudy2Title: "SF6 Gas Lifting Baskets — Energy Sector", 
    caseStudy2Desc: "Specialized solutions for safe SF6 gas handling",
    caseStudy3Title: "Motorized Hoists — Mechanical Workshop",
    caseStudy3Desc: "Improved ergonomics and risk reduction in workshop environment",
    
    // Resources
    resourcesTitle: "Guides and resources",
    guide1: "Choosing the right overhead crane",
    guide2: "Standards and compliance (CSA / ASME)",
    guide3: "Preventive maintenance checklist",
    downloadGuides: "Download our guides",
    
    // FAQ
    faq1: "Do you do custom projects?",
    faq2: "Do you offer inspection & certification?",
    faq3: "What maximum capacities do you install?",
    
    // Sectors
    manufacturing: "Manufacturing",
    manufacturingDesc: "Steel, metal, processing",
    agrifood: "Agri-food",
    agrifoodDesc: "Solutions adapted to the food industry",
    energy: "Energy",
    energyDesc: "Hydro, gas, electricity",
    transport: "Transport & logistics",
    transportDesc: "Logistics solutions",
    construction: "Building and construction",
    constructionDesc: "Equipment for the building sector",
    
    // Error messages
    error: "An error occurred"
  }
};